import { useEffect, type ReactNode } from "react";
import { ContactForm } from "./ContactForm";
import { YayLogo } from "./YayLogo";

/**
 * Reserved for when a custom domain + email lands. Until then the contact
 * form (/contact) routes submissions to the feedback worker — nothing on
 * these pages should reference this address.
 */
export const CONTACT_EMAIL = "contact@yay.example";

export const LEGAL_PAGE_PATHS = new Set<string>([
  "/terms",
  "/privacy",
  "/refund",
  "/guidelines",
  "/dmca",
  "/contact",
]);

export function isLegalPage(path: string): boolean {
  return LEGAL_PAGE_PATHS.has(path);
}

function ContactLink({
  children = "contact form",
}: {
  children?: ReactNode;
}) {
  return (
    <a href="/contact" onClick={() => window.scrollTo(0, 0)}>
      {children}
    </a>
  );
}

interface LegalSection {
  id: string;
  h: string;
  body: ReactNode;
}

interface LegalDoc {
  title: string;
  updated: string;
  intro?: ReactNode;
  sections: LegalSection[];
}

function TermsDoc(): LegalDoc {
  return {
    title: "terms of service",
    updated: "august 2026",
    intro: (
      <p>
        welcome to <strong>yay</strong> — watch-together for friends. by using
        yay (the website, the app, and the realtime rooms), you agree to these
        terms. if you don't agree, don't use yay.
      </p>
    ),
    sections: [
      {
        id: "what-yay-is",
        h: "what yay is",
        body: (
          <p>
            yay is a watch-together service. no account is required — pick a
            name and join a room. you can optionally sign in with Google.
            rooms have a shared video, chat, avatars, and presence. private
            rooms may add study tools (flashcards) and, in the future, voice
            chat.
          </p>
        ),
      },
      {
        id: "age",
        h: "who can use yay",
        body: (
          <p>
            you must be at least <strong>16 years old</strong> to use yay. if
            you are under 16, do not use yay. if you are under 18, make sure a
            parent or guardian is okay with it.
          </p>
        ),
      },
      {
        id: "identity",
        h: "identity and accounts",
        body: (
          <p>
            yay is guest-first — no account is required. your browser
            remembers your name and look across reconnects. you may
            optionally <strong>sign in with Google</strong> so the same look
            follows you on another device. signing in is optional and
            doesn&apos;t change how rooms work.
          </p>
        ),
      },
      {
        id: "acceptable-use",
        h: "acceptable use",
        body: (
          <p>
            be decent. harassment, hate, spam, illegal content, doxxing, and
            sexual content involving minors are not allowed — see the{" "}
            <a href="/guidelines">community guidelines</a> for the full rules.
            we enforce these with bans, and private rooms have moderation
            tools. the global lobby is moderated and family-friendly; private
            rooms are unrestricted by default.
          </p>
        ),
      },
      {
        id: "content-policy",
        h: "lobby content",
        body: (
          <>
            <p>
              the global lobby plays{" "}
              <strong>creative commons</strong> movies from other sites. yay
              doesn&apos;t host that video.
            </p>
            <p>
              rebroadcasting copyrighted streams is prohibited. if you see it,
              report it.
            </p>
          </>
        ),
      },
      {
        id: "stickers",
        h: "stickers and third-party content",
        body: (
          <p>
            sticker art is owned by its creators. we honor takedown requests
            — see the <a href="/dmca">copyright page</a>.
          </p>
        ),
      },
      {
        id: "paid-features",
        h: "paid features",
        body: (
          <>
            <p>
              yay has a free tier. some features may require a paid upgrade
              later.
            </p>
            <p>
              paid features are planned but <strong>not yet available</strong>.
              when they launch, payments will go through a merchant of record
              (Polar).{" "}
              <strong>your card details never touch yay</strong>. see the{" "}
              <a href="/refund">refund policy</a>.
            </p>
          </>
        ),
      },
      {
        id: "termination",
        h: "termination and bans",
        body: (
          <p>
            we may remove content and restrict access for anyone who violates
            these terms or the community guidelines, with or without notice.
          </p>
        ),
      },
      {
        id: "warranty",
        h: "no warranty, limited liability",
        body: (
          <p>
            yay is provided <strong>as is</strong>, without warranties of any
            kind. to the maximum extent permitted by law, we are not liable for
            damages arising from your use of yay — including interruptions,
            lost chat history, or content you see in rooms.
          </p>
        ),
      },
      {
        id: "changes",
        h: "changes to these terms",
        body: (
          <p>
            we may update these terms. the latest version is always on this
            page, with the date above. continuing to use yay after a change
            means you accept the updated terms.
          </p>
        ),
      },
      {
        id: "law",
        h: "governing law",
        body: (
          <p>
            these terms are governed by the laws of the country in which
            yay's operator is established. disputes will be handled in that
            country's courts.
          </p>
        ),
      },
      {
        id: "contact",
        h: "contact",
        body: (
          <p>
            questions about these terms? use the <ContactLink />.
          </p>
        ),
      },
    ],
  };
}

function PrivacyDoc(): LegalDoc {
  return {
    title: "privacy policy",
    updated: "august 2026",
    intro: (
      <p>
        the short version: yay needs no account, shows no ads, and sells no
        data. your card details never touch yay. voice chat, when it ships, is
        never recorded or stored. here&apos;s the full picture.
      </p>
    ),
    sections: [
      {
        id: "we-store",
        h: "what we store",
        body: (
          <ul>
            <li>
              <strong>username and look</strong> — so rooms can show you to
              other viewers. kept in your browser and in the room.
            </li>
            <li>
              <strong>session</strong> — a random id in your browser so your
              name stays put across reconnects. not an account.
            </li>
            <li>
              <strong>google sign-in</strong> (optional) — if you sign in, we
              keep the email (and name) Google shares, so your look can
              follow you to another device.
            </li>
            <li>
              <strong>chat, reactions, presence, playback</strong> — so the
              room works.
            </li>
            <li>
              <strong>flash cards</strong> — cards you create in study rooms.
            </li>
            <li>
              <strong>ip address</strong> — in ordinary server logs, for abuse
              protection. we don&apos;t tie it to who you are.
            </li>
            <li>
              <strong>stickers</strong> — served from our site. your browser
              doesn&apos;t fetch them from the original artists.
            </li>
            <li>
              <strong>contact and reports</strong> — messages you send us,
              including an email if you leave one.
            </li>
            <li>
              <strong>mic audio</strong> — <strong>never</strong>. we don&apos;t
              record or store voice. ever.
            </li>
          </ul>
        ),
      },
      {
        id: "retention",
        h: "how long we keep it",
        body: (
          <p>
            chat history is kept for up to <strong>90 days</strong> and is
            deleted automatically; we also keep only the latest 50 messages
            per room. other room data lives while the room is in use. want
            something gone sooner? use the <ContactLink />.
          </p>
        ),
      },
      {
        id: "third-parties",
        h: "third parties",
        body: (
          <ul>
            <li>
              <strong>Cloudflare</strong> — hosts yay, and the bot check on
              contact and feedback.
            </li>
            <li>
              <strong>Google</strong> — fonts, optional sign-in, and YouTube
              in private rooms.
            </li>
            <li>
              <strong>avatars</strong> — generated by a third-party image
              service.
            </li>
            <li>
              <strong>Discord</strong> — receives messages you send us.
            </li>
            <li>
              <strong>payments</strong> (planned) — Polar. they handle card
              data; yay never sees it.
            </li>
          </ul>
        ),
      },
      {
        id: "browser-storage",
        h: "your browser's storage",
        body: (
          <p>
            yay keeps a little data in your browser so we remember your name
            and look. clear it anytime — you&apos;ll just pick a name again.
          </p>
        ),
      },
      {
        id: "choices",
        h: "your choices",
        body: (
          <p>
            clearing your browser resets your guest look. if you signed in
            with Google and want that gone from our side, use the{" "}
            <ContactLink />.
          </p>
        ),
      },
      {
        id: "changes",
        h: "changes to this policy",
        body: (
          <p>
            we may update this policy; the date above shows the latest version.
            material changes will be called out here.
          </p>
        ),
      },
    ],
  };
}

function RefundDoc(): LegalDoc {
  return {
    title: "refund policy",
    updated: "august 2026",
    intro: (
      <p>
        yay&apos;s core is free, forever. this policy covers paid features,
        when they launch.
      </p>
    ),
    sections: [
      {
        id: "free-tier",
        h: "free tier",
        body: (
          <p>
            everything on the free tier costs nothing, so there is nothing to
            refund.
          </p>
        ),
      },
      {
        id: "purchases",
        h: "purchases",
        body: (
          <p>
            paid features are planned but <strong>not yet available</strong>.
            when purchases exist, they&apos;ll go through{" "}
            <strong>Polar</strong>, and refunds will follow Polar&apos;s
            terms — use the <ContactLink /> and we&apos;ll cooperate on our
            side.
          </p>
        ),
      },
      {
        id: "broken",
        h: "something's broken",
        body: (
          <p>
            if a paid feature doesn&apos;t work as described, use the{" "}
            <ContactLink /> within <strong>14 days</strong> of purchase and
            we&apos;ll make it right.
          </p>
        ),
      },
      {
        id: "bans",
        h: "bans",
        body: (
          <p>
            paid access lost because of a guidelines ban is not refundable.
          </p>
        ),
      },
    ],
  };
}

function GuidelinesDoc(): LegalDoc {
  return {
    title: "community guidelines",
    updated: "august 2026",
    intro: (
      <p>
        yay is for watching together. keep it decent and these rooms stay
        fun. we enforce these rules with bans, and private rooms have
        moderation tools.
      </p>
    ),
    sections: [
      {
        id: "be-decent",
        h: "be decent",
        body: (
          <ul>
            <li>no harassment, hate speech, or bullying — of anyone, ever.</li>
            <li>
              no sexual content involving minors. zero tolerance; reported
              immediately.
            </li>
            <li>no doxxing, impersonation, or sharing others' private info.</li>
            <li>no spam, flooding, or self-promotion spam in chat.</li>
            <li>no illegal content, and no content promoting self-harm.</li>
            <li>
              no malicious links or attempts to break the service.
            </li>
          </ul>
        ),
      },
      {
        id: "global-lobby",
        h: "the global lobby",
        body: (
          <p>
            the global lobby is a safe, family-friendly space. it plays
            creative commons movies and{" "}
            <strong>voice chat is never available</strong> there. private
            rooms are the opposite: unrestricted by default.
          </p>
        ),
      },
      {
        id: "copyright",
        h: "copyright and lobby content",
        body: (
          <p>
            the lobby plays creative commons movies from other sites — yay
            doesn&apos;t host that video. rebroadcasting copyrighted streams
            is prohibited. private rooms are for content you have the right
            to watch together.
          </p>
        ),
      },
      {
        id: "voice",
        h: "voice chat (when available)",
        body: (
          <ul>
            <li>
              voice chat isn&apos;t here yet — when it ships it will be{" "}
              <strong>private rooms only</strong>, never the public lobby.
            </li>
            <li>
              never record voice calls. yay doesn&apos;t record them either.
            </li>
          </ul>
        ),
      },
      {
        id: "enforcement",
        h: "enforcement",
        body: (
          <p>
            we may restrict access for violations. we aren&apos;t obligated
            to explain every action, and we don&apos;t have to warn before
            acting.
          </p>
        ),
      },
      {
        id: "reporting",
        h: "reporting and appeals",
        body: (
          <p>
            if you see something that breaks these rules, use the report
            button on the offending message in the app, or tell us via the{" "}
            <ContactLink /> with the room name and a rough time. if you believe
            a restriction was a mistake, use the <ContactLink /> too.
          </p>
        ),
      },
    ],
  };
}

function DmcaDoc(): LegalDoc {
  return {
    title: "copyright & takedowns",
    updated: "august 2026",
    intro: (
      <p>
        the lobby plays <strong>creative commons</strong> movies from other
        sites — yay doesn&apos;t host that video. if something still needs
        to come down, here&apos;s how.
      </p>
    ),
    sections: [
      {
        id: "notice",
        h: "filing a notice",
        body: (
          <>
            <p>
              if you believe content on yay infringes your copyright, use the{" "}
              <ContactLink /> with the subject <strong>DMCA</strong> and
              include:
            </p>
            <ul>
              <li>your name and contact information (email, phone).</li>
              <li>
                a description of the copyrighted work you believe is
                infringed.
              </li>
              <li>
                the exact location on yay (the room or URL) where the content
                appears.
              </li>
              <li>
                a statement of good faith: you believe the use is not
                authorized by the owner, their agent, or the law.
              </li>
              <li>
                a statement, under penalty of perjury, that the information in
                the notice is accurate and you are the rights holder or
                authorized to act for them.
              </li>
              <li>your physical or electronic signature.</li>
            </ul>
          </>
        ),
      },
      {
        id: "counter",
        h: "counter-notice",
        body: (
          <p>
            if content you posted was removed by mistake, you can respond with
            a counter-notice: your contact info, what was removed and where,
            and a statement under penalty of perjury that the removal was a
            mistake or misidentification. we'll review and restore where
            appropriate.
          </p>
        ),
      },
      {
        id: "sticker-attribution",
        h: "sticker attribution",
        body: (
          <p>
            sticker art is licensed by its creators. we&apos;ll remove
            anything that isn&apos;t allowed, on request.
          </p>
        ),
      },
    ],
  };
}

function ContactDoc(): LegalDoc {
  return {
    title: "contact & support",
    updated: "august 2026",
    intro: (
      <p>
        a human reads these messages. yay is a small project, so replies may
        take a few days.
      </p>
    ),
    sections: [
      {
        id: "reporting",
        h: "reporting abuse",
        body: (
          <p>
            see something that breaks the rules? use the form above with the
            room name and a rough time — include the message if you can.
          </p>
        ),
      },
      {
        id: "deletion",
        h: "deletion requests",
        body: (
          <p>
            want something deleted? use the form above and we&apos;ll handle
            it.
          </p>
        ),
      },
      {
        id: "feedback",
        h: "feedback",
        body: (
          <p>
            in the app, the feedback button in the top bar sends a message
            straight to us. the form above works too.
          </p>
        ),
      },
    ],
  };
}

const DOCS: Record<string, () => LegalDoc> = {
  "/terms": TermsDoc,
  "/privacy": PrivacyDoc,
  "/refund": RefundDoc,
  "/guidelines": GuidelinesDoc,
  "/dmca": DmcaDoc,
  "/contact": ContactDoc,
};

export function LegalPage() {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  const doc = (DOCS[path] ?? TermsDoc)();

  useEffect(() => {
    document.title = `${doc.title} — yay`;
  }, [doc.title]);

  return (
    <div className="legal-page">
      <header className="legal-header">
        <a className="legal-home" href="/" aria-label="back to yay">
          <YayLogo variant="purple" className="legal-logo" alt="yay" />
        </a>
        <a className="legal-enter" href="/">
          <span className="legal-enter-text">enter</span>{" "}
          <YayLogo variant="white" className="legal-enter-word" alt="" />
        </a>
      </header>

      <main className="legal-content">
        <h1 className="legal-title">{doc.title}</h1>
        <p className="legal-updated">last updated: {doc.updated}</p>
        {doc.intro && <div className="legal-intro">{doc.intro}</div>}
        {path === "/contact" && <ContactForm />}
        {doc.sections.map((s) => (
          <section key={s.id} className="legal-section" id={s.id}>
            <h2>{s.h}</h2>
            {s.body}
          </section>
        ))}
      </main>

      <footer className="legal-footer">
        <a href="/home">home</a>
        <a href="/terms">terms</a>
        <a href="/privacy">privacy</a>
        <a href="/refund">refunds</a>
        <a href="/guidelines">guidelines</a>
        <a href="/dmca">copyright</a>
        <a href="/contact">contact</a>
        <span className="legal-copyright">© 2026 yay</span>
      </footer>
    </div>
  );
}
